/**
 * Editor-wide Open/Resolved/All filter for comments.
 *
 * The filter lives above `CommentsProvider` because two panes (source
 * and translation) each mount their own `CommentsProvider`, and both
 * need to reflect the same header switch. Sits at `TranscriptMain`
 * level so `TranscriptAbout` (which renders the segmented control) and
 * both `TranscriptBody` instances share one state.
 *
 * `useCommentFilter` returns a safe no-op default when no provider is
 * mounted so isolated leaves (shared editor, unit tests) keep the
 * pre-filter behavior of showing open comments.
 */

import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

export type CommentFilter = 'open' | 'resolved' | 'all';

interface CommentFilterContextValue {
  filter: CommentFilter;
  setFilter: (next: CommentFilter) => void;
}

const CommentFilterContext = createContext<CommentFilterContextValue | null>(
  null
);

export function useCommentFilter(): CommentFilterContextValue {
  const ctx = useContext(CommentFilterContext);
  return (
    ctx ?? {
      filter: 'open',
      setFilter: () => undefined
    }
  );
}

export function CommentFilterProvider({ children }: { children: ReactNode }) {
  const [filter, setFilter] = useState<CommentFilter>('open');
  const value = useMemo(() => ({ filter, setFilter }), [filter]);
  return (
    <CommentFilterContext.Provider value={value}>
      {children}
    </CommentFilterContext.Provider>
  );
}
